import { Singleton } from '@utils/singleton';
import {
  INITIAL_WIDTH,
  INITIAL_HEIGHT,
  LAYOUT_DEPTH,
} from '@constants/common.const';
import { AssetKey } from '@constants/asset-keys.const';

@Singleton
export class LayoutService {
  public readonly isMobile: boolean;

  // Design resolution used internally for layout math (mobile or desktop)
  private readonly designWidth: number;
  private readonly designHeight: number;

  constructor(private scale: Phaser.Scale.ScaleManager) {
    this.isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    // Mobile uses real fullscreen dimensions
    // Desktop uses fixed 1280x720 design area.
    if (this.isMobile) {
      this.designWidth = this.scale.width; // fullscreen width
      this.designHeight = this.scale.height; // fullscreen height
    } else {
      this.designWidth = INITIAL_WIDTH;
      this.designHeight = INITIAL_HEIGHT;
    }
  }

  // --- screen dimensions ---
  get screenWidth(): number {
    return this.scale.width;
  }

  get screenHeight(): number {
    return this.scale.height;
  }

  // --- scale ratios for responsive math ---
  get scaleX(): number {
    return this.screenWidth / this.designWidth;
  }

  get scaleY(): number {
    return this.screenHeight / this.designHeight;
  }

  // --- coordinate helpers ---
  centerX(offset = 0): number {
    return this.screenWidth / 2 + offset;
  }

  centerY(offset = 0): number {
    return this.screenHeight / 2 + offset;
  }

  left(padding = 0): number {
    return padding;
  }

  right(padding = 0): number {
    return this.screenWidth - padding;
  }

  top(padding = 0): number {
    return padding;
  }

  bottom(padding = 0): number {
    return this.screenHeight - padding;
  }

  createHomeBackground(scene: Phaser.Scene): {
    bg: Phaser.GameObjects.Image;
    cleanup: () => void;
  } {
    return this.createCoverBackground(scene, AssetKey.HomeBg);
  }

  /**
   * Create a static, viewport-covering background image.
   * - Pinned to the camera (scrollFactor 0) so it never pans with the world.
   * - Scaled to cover the viewport (CSS background-size: cover) — it is
   *   sized to the screen only, never stretched to the world height.
   */
  createGameBackground(scene: Phaser.Scene): {
    bg: Phaser.GameObjects.Image;
    cleanup: () => void;
  } {
    return this.createCoverBackground(scene, AssetKey.GameBg);
  }

  private createCoverBackground(
    scene: Phaser.Scene,
    key: string,
  ): {
    bg: Phaser.GameObjects.Image;
    cleanup: () => void;
  } {
    const bg = scene.add
      .image(0, 0, key)
      .setOrigin(0, 0)
      .setDepth(LAYOUT_DEPTH)
      .setScrollFactor(0);

    const resize = () => {
      const screenW = this.screenWidth;
      const screenH = this.screenHeight;

      const tex = scene.textures.get(key).getSourceImage();
      const imgW = tex.width;
      const imgH = tex.height;

      // Calculate scale to cover the screen (CSS background-size: cover)
      const scale = Math.max(screenW / imgW, screenH / imgH);

      bg.setScale(scale);
      bg.setPosition(0, 0);
    };

    // Initial resize
    resize();

    // Handle dynamic resize
    scene.scale.on('resize', resize, scene);

    const cleanup = () => {
      scene.scale.off('resize', resize, scene);
    };

    return { bg, cleanup };
  }
}
